import { useEffect } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
  type UseQueryOptions,
} from "@tanstack/react-query";

import { useToast } from "@/shared/hooks/use-toast";
import { paymentApi } from "../api";
import type {
  Payment,
  PaginatedPaymentsResponse,
  GetPaymentsParams,
  CreatePaymentPayload,
  UpdatePaymentPayload,
  PaymentDocument,
  CreatePaymentDocumentPayload,
  UpdatePaymentDocumentPayload,
  PaymentCreatorBankAccount,
  CreateCreatorBankAccountPayload,
  PaginatedCreatorBankAccountsResponse,
} from "../utils/types";
import type { Creator } from "@/features/creators/services/creatorService";
import { paymentKeys } from "../utils/payment.keys";

export function usePayments(
  filters?: GetPaymentsParams,
  options?: UseQueryOptions<PaginatedPaymentsResponse, Error>,
) {
  const { toast } = useToast();

  const query = useQuery({
    queryKey: paymentKeys.list(filters),
    queryFn: ({ signal }) => paymentApi.list(filters, signal),
    staleTime: 60 * 1000,
    ...options,
  });

  useEffect(() => {
    if (!query.error) return;
    toast({
      title: "Erro ao carregar pagamentos",
      description: query.error.message,
      variant: "destructive",
    });
  }, [query.error, toast]);

  return {
    payments: query.data?.data ?? [],
    page: query.data?.page ?? 1,
    limit: query.data?.limit ?? filters?.limit ?? 10,
    totalItems: query.data?.totalItems ?? 0,
    totalPages: query.data?.totalPage ?? 1,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    refetch: query.refetch,
    queryResult: query,
  };
}

export function usePayment(
  id?: string,
  options?: UseQueryOptions<Payment, Error>,
) {
  const { toast } = useToast();

  const query = useQuery({
    queryKey: id ? paymentKeys.detail(id) : [...paymentKeys.details(), ''],
    queryFn: ({ signal }) => {
      if (!id) throw new Error("ID do pagamento não informado");
      return paymentApi.get(id, signal);
    },
    enabled: Boolean(id),
    staleTime: 60 * 1000,
    ...options,
  });

  useEffect(() => {
    if (!query.error) return;
    toast({
      title: "Erro ao carregar pagamento",
      description: query.error.message,
      variant: "destructive",
    });
  }, [query.error, toast]);

  return {
    payment: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    refetch: query.refetch,
    queryResult: query,
  };
}

type CreatePaymentVariables = CreatePaymentPayload;

export function useCreatePayment(
  options?: UseMutationOptions<Payment, Error, CreatePaymentVariables>,
) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const mutation = useMutation({
    mutationKey: ["payments", "create"],
    mutationFn: paymentApi.create,
    ...options,
    onSuccess: (payment, variables, context) => {
      toast({
        title: "Pagamento criado com sucesso",
        variant: "success",
      });
      queryClient.invalidateQueries({ queryKey: paymentKeys.all });
      options?.onSuccess?.(payment, variables, context);
    },
    onError: (error, variables, context) => {
      toast({
        title: "Erro ao criar pagamento",
        description: error.message,
        variant: "destructive",
      });
      options?.onError?.(error, variables, context);
    },
  });

  return {
    createPayment: mutation.mutateAsync,
    isLoading: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
    mutationResult: mutation,
  };
}

type UpdatePaymentVariables = UpdatePaymentPayload;

export function useUpdatePayment(
  options?: UseMutationOptions<Payment, Error, UpdatePaymentVariables>,
) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const mutation = useMutation({
    mutationKey: ["payments", "update"],
    mutationFn: paymentApi.update,
    ...options,
    onSuccess: (payment, variables, context) => {
      toast({
        title: "Pagamento atualizado com sucesso",
        variant: "success",
      });
      queryClient.invalidateQueries({ queryKey: paymentKeys.all });
      queryClient.invalidateQueries({ queryKey: paymentKeys.detail(variables.id) });
      options?.onSuccess?.(payment, variables, context);
    },
    onError: (error, variables, context) => {
      toast({
        title: "Erro ao atualizar pagamento",
        description: error.message,
        variant: "destructive",
      });
      options?.onError?.(error, variables, context);
    },
  });

  return {
    updatePayment: mutation.mutateAsync,
    isLoading: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
    mutationResult: mutation,
  };
}

interface DeletePaymentVariables {
  id: string;
}

export function useDeletePayment(
  options?: UseMutationOptions<{ id: string; deletedAt?: string }, Error, DeletePaymentVariables>,
) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const mutation = useMutation({
    mutationKey: ["payments", "delete"],
    mutationFn: ({ id }: DeletePaymentVariables) => paymentApi.remove(id),
    ...options,
    onSuccess: (response, variables, context) => {
      toast({
        title: "Pagamento removido com sucesso",
        variant: "success",
      });
      queryClient.invalidateQueries({ queryKey: paymentKeys.all });
      options?.onSuccess?.(response, variables, context);
    },
    onError: (error, variables, context) => {
      toast({
        title: "Erro ao remover pagamento",
        description: error.message,
        variant: "destructive",
      });
      options?.onError?.(error, variables, context);
    },
  });

  return {
    deletePayment: mutation.mutateAsync,
    isLoading: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
    mutationResult: mutation,
  };
}

export function usePaymentDocuments(
  paymentId?: string,
  options?: UseQueryOptions<PaymentDocument[], Error>,
) {
  const { toast } = useToast();

  const query = useQuery({
    queryKey: paymentId ? paymentKeys.documents(paymentId) : [...paymentKeys.all, 'documents', ''],
    queryFn: ({ signal }) => {
      if (!paymentId) throw new Error("ID do pagamento não informado");
      return paymentApi.documents.list(paymentId, signal);
    },
    enabled: Boolean(paymentId),
    staleTime: 60 * 1000,
    ...options,
  });

  useEffect(() => {
    if (!query.error) return;
    toast({
      title: "Erro ao carregar documentos do pagamento",
      description: query.error.message,
      variant: "destructive",
    });
  }, [query.error, toast]);

  return {
    documents: query.data ?? [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    refetch: query.refetch,
    queryResult: query,
  };
}

export function useCreatePaymentDocument(
  options?: UseMutationOptions<PaymentDocument, Error, CreatePaymentDocumentPayload>,
) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const mutation = useMutation({
    mutationKey: ["payments", "documents", "create"],
    mutationFn: paymentApi.documents.create,
    ...options,
    onSuccess: (document, variables, context) => {
      toast({
        title: "Documento anexado com sucesso",
        variant: "success",
      });
      // Invalidar apenas a query de documentos do pagamento específico
      // Não invalidar a lista geral de pagamentos para evitar navegação indesejada
      queryClient.invalidateQueries({ queryKey: paymentKeys.documents(variables.paymentId) });
      options?.onSuccess?.(document, variables, context);
    },
    onError: (error, variables, context) => {
      toast({
        title: "Erro ao anexar documento",
        description: error.message,
        variant: "destructive",
      });
      options?.onError?.(error, variables, context);
    },
  });

  return {
    createPaymentDocument: mutation.mutateAsync,
    isLoading: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
    mutationResult: mutation,
  };
}

type UpdatePaymentDocumentVariables = UpdatePaymentDocumentPayload & { paymentId: string };

export function useUpdatePaymentDocument(
  options?: UseMutationOptions<PaymentDocument, Error, UpdatePaymentDocumentVariables>,
) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const mutation = useMutation({
    mutationKey: ["payments", "documents", "update"],
    mutationFn: (variables: UpdatePaymentDocumentVariables) => paymentApi.documents.update(variables),
    ...options,
    onSuccess: (document, variables, context) => {
      toast({
        title: "Documento atualizado com sucesso",
        variant: "success",
      });
      queryClient.invalidateQueries({ queryKey: paymentKeys.documents(variables.paymentId) });
      queryClient.invalidateQueries({ queryKey: paymentKeys.all });
      options?.onSuccess?.(document, variables, context);
    },
    onError: (error, variables, context) => {
      toast({
        title: "Erro ao atualizar documento",
        description: error.message,
        variant: "destructive",
      });
      options?.onError?.(error, variables, context);
    },
  });

  return {
    updatePaymentDocument: mutation.mutateAsync,
    isLoading: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
    mutationResult: mutation,
  };
}

interface DeletePaymentDocumentVariables {
  id: string;
  paymentId: string;
}

export function useDeletePaymentDocument(
  options?: UseMutationOptions<{ id: string; deletedAt?: string }, Error, DeletePaymentDocumentVariables>,
) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const mutation = useMutation({
    mutationKey: ["payments", "documents", "delete"],
    mutationFn: ({ id, paymentId }: DeletePaymentDocumentVariables) => paymentApi.documents.remove(paymentId, id),
    ...options,
    onSuccess: (response, variables, context) => {
      toast({
        title: "Documento removido com sucesso",
        variant: "success",
      });
      queryClient.invalidateQueries({ queryKey: paymentKeys.documents(variables.paymentId) });
      queryClient.invalidateQueries({ queryKey: paymentKeys.all });
      options?.onSuccess?.(response, variables, context);
    },
    onError: (error, variables, context) => {
      toast({
        title: "Erro ao remover documento",
        description: error.message,
        variant: "destructive",
      });
      options?.onError?.(error, variables, context);
    },
  });

  return {
    deletePaymentDocument: mutation.mutateAsync,
    isLoading: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
    mutationResult: mutation,
  };
}

export function usePaymentCreators(options?: UseQueryOptions<Creator[], Error>) {
  const { toast } = useToast();

  const query = useQuery({
    queryKey: paymentKeys.creators(),
    queryFn: ({ signal }) => paymentApi.creators.list(signal),
    staleTime: 5 * 60 * 1000,
    ...options,
  });

  useEffect(() => {
    if (!query.error) return;
    toast({
      title: "Erro ao carregar creators",
      description: query.error.message,
      variant: "destructive",
    });
  }, [query.error, toast]);

  return {
    creators: query.data ?? [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    refetch: query.refetch,
    queryResult: query,
  };
}

export function useCreatorBankAccounts(
  creatorId?: string,
  options?: UseQueryOptions<PaginatedCreatorBankAccountsResponse, Error>,
) {
  const { toast } = useToast();

  const query = useQuery({
    queryKey: creatorId ? paymentKeys.bankAccounts(creatorId) : [...paymentKeys.all, 'bank-accounts', ''],
    queryFn: ({ signal }) => {
      if (!creatorId) throw new Error("ID do creator não informado");
      return paymentApi.bankAccounts.list({ creatorId, limit: 100 }, signal);
    },
    enabled: Boolean(creatorId),
    staleTime: 5 * 60 * 1000,
    ...options,
  });

  useEffect(() => {
    if (!query.error) return;
    toast({
      title: "Erro ao carregar contas bancárias",
      description: query.error.message,
      variant: "destructive",
    });
  }, [query.error, toast]);

  return {
    bankAccounts: query.data?.data ?? [],
    totalItems: query.data?.totalItems ?? 0,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    refetch: query.refetch,
    queryResult: query,
  };
}

export function useCreateCreatorBankAccount(
  options?: UseMutationOptions<PaymentCreatorBankAccount, Error, CreateCreatorBankAccountPayload>,
) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const mutation = useMutation({
    mutationKey: ["payments", "bank-accounts", "create"],
    mutationFn: paymentApi.bankAccounts.create,
    ...options,
    onSuccess: (account, variables, context) => {
      toast({
        title: "Conta bancária criada com sucesso",
        variant: "success",
      });
      queryClient.invalidateQueries({ queryKey: paymentKeys.bankAccounts(variables.creatorId) });
      options?.onSuccess?.(account, variables, context);
    },
    onError: (error, variables, context) => {
      toast({
        title: "Erro ao criar conta bancária",
        description: error.message,
        variant: "destructive",
      });
      options?.onError?.(error, variables, context);
    },
  });

  return {
    createCreatorBankAccount: mutation.mutateAsync,
    isLoading: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
    mutationResult: mutation,
  };
}
